import React from "react";
import { Card, Statistic, Row, Col, Progress, Badge, Tooltip, Space, Typography, Divider } from "antd";
import {
  ClockCircleOutlined,
  SendOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text, Title } = Typography;

const SequenceQueueOverview = ({ queueData }) => {
  if (!queueData) {
    return (
      <Card style={{ borderRadius: "12px" }}>
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <SyncOutlined spin style={{ fontSize: "24px", color: "#1890ff" }} />
          <Text style={{ display: "block", marginTop: "8px" }}>Loading queue status...</Text>
        </div>
      </Card>
    );
  }

  const { queue, processing } = queueData;

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return "#52c41a";
    if (rate >= 85) return "#faad14";
    return "#f5222d";
  };

  const getSuccessRateStatus = (rate) => {
    if (rate >= 95) return "success";
    if (rate >= 85) return "warning";
    return "error";
  };

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Space>
            <ThunderboltOutlined style={{ color: "#1890ff" }} />
            <Title level={4} style={{ margin: 0 }}>Sequence Processing Queue</Title>
          </Space>
          
          <div style={{ 
            display: "flex", 
            alignItems: "center",
            padding: "6px 10px",
            background: "#f6ffed",
            borderRadius: "4px",
            border: "1px solid #b7eb8f",
            fontSize: "11px"
          }}>
            <SyncOutlined style={{ color: "#52c41a", marginRight: "4px", fontSize: "11px" }} />
            <Text style={{ fontSize: "11px", color: "#52c41a" }}>Live Data</Text>
          </div>
        </div>
      }
      style={{ borderRadius: "12px", marginBottom: "20px" }}
      bodyStyle={{ padding: "24px" }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        
        {/* Queue Status Row */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card 
              size="small" 
              style={{ 
                background: queue.dueNow > 0 ? "#fff2e8" : "#f6ffed",
                border: queue.dueNow > 0 ? "1px solid #ffbb96" : "1px solid #b7eb8f"
              }}
            >
              <Statistic
                title={
                  <Space>
                    <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />
                    <span>Due Now</span>
                  </Space>
                }
                value={queue.dueNow || 0}
                valueStyle={{ 
                  color: queue.dueNow > 0 ? "#fa8c16" : "#52c41a",
                  fontWeight: "bold"
                }}
                suffix={queue.dueNow > 0 && (
                  <Tooltip title="Emails that should be sent immediately">
                    <Badge status="processing" />
                  </Tooltip>
                )}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ background: "#f0f2ff", border: "1px solid #adc6ff" }}>
              <Statistic
                title={
                  <Space>
                    <ClockCircleOutlined style={{ color: "#1890ff" }} />
                    <span>Pending</span>
                  </Space>
                }
                value={queue.pending || 0}
                valueStyle={{ color: "#1890ff", fontWeight: "bold" }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ background: "#f9f0ff", border: "1px solid #d3adf7" }}>
              <Statistic
                title={
                  <Space>
                    <SyncOutlined style={{ color: "#722ed1" }} />
                    <span>In Progress</span>
                  </Space>
                }
                value={queue.inProgress || 0}
                valueStyle={{ color: "#722ed1", fontWeight: "bold" }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card size="small" style={{ background: "#f0f9ff", border: "1px solid #91d5ff" }}>
              <Statistic
                title={
                  <Space>
                    <CalendarOutlined style={{ color: "#13c2c2" }} />
                    <span>Next Hour</span>
                  </Space>
                }
                value={queue.scheduledNextHour || 0}
                valueStyle={{ color: "#13c2c2", fontWeight: "bold" }}
              />
            </Card>
          </Col>
        </Row>

        <Divider style={{ margin: "16px 0" }} />

        {/* Processing Statistics */}
        <Row gutter={[24, 16]}>
          <Col xs={24} lg={12}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong style={{ fontSize: "16px" }}>
                <SendOutlined /> Processing Performance (Last Hour)
              </Text>
              
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total Sent"
                    value={processing.lastHour || 0}
                    valueStyle={{ fontSize: "20px", color: "#1890ff" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Successful"
                    value={processing.successful || 0}
                    valueStyle={{ fontSize: "20px", color: "#52c41a" }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Failed"
                    value={processing.failed || 0}
                    valueStyle={{ fontSize: "20px", color: "#f5222d" }}
                  />
                </Col>
              </Row>
            </Space>
          </Col>

          <Col xs={24} lg={12}>
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Text strong style={{ fontSize: "16px" }}>
                <CheckCircleOutlined /> Success Rate
              </Text>
              
              <div style={{ textAlign: "center" }}>
                <Progress
                  type="circle"
                  percent={Math.round(processing.successRate || 0)}
                  strokeColor={getSuccessRateColor(processing.successRate || 0)}
                  format={(percent) => (
                    <div>
                      <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                        {percent}%
                      </div>
                      <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                        Success
                      </div>
                    </div>
                  )}
                  width={100}
                />
                
                <div style={{ marginTop: "12px" }}>
                  <Badge 
                    status={getSuccessRateStatus(processing.successRate || 0)}
                    text={
                      processing.successRate >= 95 ? "Excellent" :
                      processing.successRate >= 85 ? "Good" : "Needs Attention"
                    }
                  />
                </div>
              </div>
            </Space>
          </Col>
        </Row>

        {/* Alert Messages */}
        {queue.dueNow > 10 && (
          <div style={{
            background: "#fff7e6",
            border: "1px solid #ffd591",
            borderRadius: "6px",
            padding: "12px"
          }}>
            <Space>
              <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />
              <Text strong style={{ color: "#d46b08" }}>
                High Queue Alert: {queue.dueNow} emails are waiting to be processed
              </Text>
            </Space>
          </div>
        )}

        {processing.successRate < 85 && processing.lastHour > 0 && (
          <div style={{
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: "6px",
            padding: "12px"
          }}>
            <Space>
              <ExclamationCircleOutlined style={{ color: "#f5222d" }} />
              <Text strong style={{ color: "#cf1322" }}>
                Low Success Rate: {Math.round(processing.successRate)}% delivery rate needs attention
              </Text>
            </Space>
          </div>
        )}

        {/* Last Updated */}
        <div style={{ textAlign: "center", paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Last updated: {queueData.lastUpdated && moment(queueData.lastUpdated).isValid() ? moment(queueData.lastUpdated).format("HH:mm:ss") : "N/A"}
          </Text>
        </div>

      </Space>
    </Card>
  );
};

export default SequenceQueueOverview;